import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getUserAttempts, getAllAttempts, type Attempt } from "@/lib/rounding";

const Home: React.FC = () => {
  const { user, logout } = useAuth();

  const [loadingStats, setLoadingStats] = useState(false);
  type CategoryStats = {
    totalAttempts: number;
    totalCompletedTasks: number;
    todayDone: number;
    todayCorrect: number;
    todayIncorrect: number;
    timeMsToday: number;
    timeSpentStr: string;
  };
  const [stats, setStats] = useState<{ rounding: CategoryStats; conversion: CategoryStats; multiplication: CategoryStats } | null>(null);

  const categories = [
    { title: "Math - Rounding", path: "/rounding", description: "Practice rounding numbers to various units and decimal places" },
    { title: "Math - Conversion", path: "/conversion", description: "Practice common unit conversions (mL→L, m→km, mm→m, etc.)" },
    { title: "Math - Multiplication", path: "/multiplication", description: "Practice multiplication problems (1–9 × 1–9)" },
  ];

  useEffect(() => {
    let mounted = true;
    async function loadStats() {
      setLoadingStats(true);
      try {
        const attempts: Attempt[] = user ? await getUserAttempts(user.username) : await getAllAttempts();
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

        const formatTime = (ms: number) => {
          if (!ms) return "0s";
          const sec = Math.round(ms / 1000);
          const mins = Math.floor(sec / 60);
          const secs = sec % 60;
          return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
        };

        const roundingAttempts = attempts.filter((a) => {
          const t = (a.rule as any)?.type;
          return t === "nearest" || t === "decimals";
        });
        const conversionAttempts = attempts.filter((a) => (a.rule as any)?.type === "conversion");
        const multiplicationAttempts = attempts.filter((a) => (a.rule as any)?.type === "multiplication");

        function computeFor(arr: Attempt[]): CategoryStats {
          const totalAttempts = arr.length;
          const totalCompletedTasks = new Set(arr.filter((a) => a.correct).map((a) => a.taskId)).size;
          const todayAttempts = arr.filter((a) => (a.timestamp || 0) >= startOfToday);
          const todayDone = todayAttempts.length;
          const todayCorrect = todayAttempts.filter((a) => a.correct).length;
          const todayIncorrect = todayDone - todayCorrect;
          const timeMsToday = todayAttempts.reduce((s, a) => s + (a.timeMs || 0), 0);
          return {
            totalAttempts,
            totalCompletedTasks,
            todayDone,
            todayCorrect,
            todayIncorrect,
            timeMsToday,
            timeSpentStr: formatTime(timeMsToday),
          };
        }

        if (mounted) {
          setStats({ rounding: computeFor(roundingAttempts), conversion: computeFor(conversionAttempts), multiplication: computeFor(multiplicationAttempts) });
        }
      } catch (e) {
        // ignore errors and leave stats null
      } finally {
        if (mounted) setLoadingStats(false);
      }
    }

    loadStats();
    return () => { mounted = false };
  }, [user]);

  return (
    <div className="w-full mt-8 px-4">
      <Card>
        <CardHeader>
          <CardTitle>Home</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="mb-4">
            {user ? (
              <div className="flex items-center justify-between">
                <p>
                  Signed in as <strong>{user.username}</strong> ({user.role})
                </p>
                <Button variant="outline" onClick={() => logout()}>
                  Logout
                </Button>
              </div>
            ) : (
              <Button asChild>
                <Link to="/login">Sign in</Link>
              </Button>
            )}
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-medium">Your Stats</h3>
            <div className="grid gap-3 md:grid-cols-3 mb-4">
              <div className="rounded-md border p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Rounding</div>
                    <div className="text-xs text-muted-foreground">Rounding tasks</div>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-3 mt-3">
                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">Total tasks completed (correct)</div>
                    <div className="text-2xl font-semibold">{loadingStats ? "…" : stats ? stats.rounding.totalCompletedTasks : "—"}</div>
                    <div className="text-xs text-muted-foreground">Total attempts: {loadingStats ? "…" : stats ? stats.rounding.totalAttempts : "—"}</div>
                  </div>

                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">Today — done / correct / incorrect</div>
                    <div className="text-2xl font-semibold">{loadingStats ? "…" : stats ? `${stats.rounding.todayDone} / ${stats.rounding.todayCorrect} / ${stats.rounding.todayIncorrect}` : "—"}</div>
                  </div>

                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">Approx. time spent today</div>
                    <div className="text-2xl font-semibold">{loadingStats ? "…" : stats ? stats.rounding.timeSpentStr : "—"}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-md border p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Conversion</div>
                    <div className="text-xs text-muted-foreground">Conversion tasks</div>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-3 mt-3">
                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">Total tasks completed (correct)</div>
                    <div className="text-2xl font-semibold">{loadingStats ? "…" : stats ? stats.conversion.totalCompletedTasks : "—"}</div>
                    <div className="text-xs text-muted-foreground">Total attempts: {loadingStats ? "…" : stats ? stats.conversion.totalAttempts : "—"}</div>
                  </div>

                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">Today — done / correct / incorrect</div>
                    <div className="text-2xl font-semibold">{loadingStats ? "…" : stats ? `${stats.conversion.todayDone} / ${stats.conversion.todayCorrect} / ${stats.conversion.todayIncorrect}` : "—"}</div>
                  </div>

                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">Approx. time spent today</div>
                    <div className="text-2xl font-semibold">{loadingStats ? "…" : stats ? stats.conversion.timeSpentStr : "—"}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-md border p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Multiplication</div>
                    <div className="text-xs text-muted-foreground">Multiplication tasks (1–9 × 1–9)</div>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-3 mt-3">
                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">Total tasks completed (correct)</div>
                    <div className="text-2xl font-semibold">{loadingStats ? "…" : stats ? stats.multiplication.totalCompletedTasks : "—"}</div>
                    <div className="text-xs text-muted-foreground">Total attempts: {loadingStats ? "…" : stats ? stats.multiplication.totalAttempts : "—"}</div>
                  </div>

                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">Today — done / correct / incorrect</div>
                    <div className="text-2xl font-semibold">{loadingStats ? "…" : stats ? `${stats.multiplication.todayDone} / ${stats.multiplication.todayCorrect} / ${stats.multiplication.todayIncorrect}` : "—"}</div>
                  </div>

                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">Approx. time spent today</div>
                    <div className="text-2xl font-semibold">{loadingStats ? "…" : stats ? stats.multiplication.timeSpentStr : "—"}</div>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-sm font-medium">Available Categories</h3>
            <div className="grid gap-3 md:grid-cols-2">
              {categories.map((c) => (
                <div key={c.path} className="rounded-md border p-3 flex flex-col justify-between">
                  <div>
                    <div className="font-semibold">{c.title}</div>
                    <div className="text-sm text-muted-foreground">{c.description}</div>
                  </div>
                  <div className="mt-3">
                    <Button asChild>
                      <Link to={c.path}>Open</Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Home;
